import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { LogoMark, LogoFull } from '@/components/Logo'
import Testimonial from '@/components/ui/testimonial'

// FAQ Item Component
const FAQItem = ({ question, answer, icon }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden hover:bg-neutral-50 transition-colors duration-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 text-left flex items-center justify-between"
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${question.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-neutral-100 text-neutral-600 flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 pr-4">
            {question}
          </h3>
        </div>
        <div className={`w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
          isOpen ? 'rotate-180 bg-orange-100 text-orange-600' : 'text-neutral-500'
        }`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      
      <div 
        id={`faq-answer-${question.replace(/\s+/g, '-').toLowerCase()}`}
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
        aria-hidden={!isOpen}
      >
        <div className="px-6 pb-5">
          <div className="pl-14">
            <p className="text-neutral-600 leading-relaxed">
              {answer}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Animated section component inspired by Motion.dev
const AnimatedSection = ({ children, className = "", delay = 0, as: Component = "section", ...rest }) => {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay)
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [delay])

  return (
    <Component
      ref={ref}
      className={`transition-all duration-500 ease-out ${
        isVisible 
          ? 'opacity-100 translate-y-0 scale-100' 
          : 'opacity-0 translate-y-8 scale-95'
      } ${className}`}
      {...rest}
    >
      {children}
    </Component>
  )
}

export default function LandingPage() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeService, setActiveService] = useState('designs')
  const [progress, setProgress] = useState(0)
  const [isHovering, setIsHovering] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  
  // Slider state
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [currentX, setCurrentX] = useState(0)
  const [scrollProgress, setScrollProgress] = useState(0)
  const sliderRef = useRef(null)
  

  const services = useMemo(() => ['designs', 'ui-ux', 'results', 'testing'], [])
  const serviceIndex = services.indexOf(activeService)
  
  const projects = useMemo(() => [
    {
      title: 'Landing pages for Amazon',
      year: '2025',
      body: 'Amazon required a new series of landing pages for onboarding new sellers to their platform via a webinar',
      image: '/assets/images/mockups/THUMB-1.png',
      hasImage: true,
      figmaUrl: 'https://www.figma.com/deck/77PTu4QZZA84GW6d67E3GR/-PUBLIC--Dom-Murphy---Case-Studies-OCT-2025?node-id=1-108&t=wsKTALmEeHxEYry8-1'
    },
    {
      title: 'Webflow Website for Hälsa',
      year: '2025',
      body: 'Europe\'s leading Chiropractic needed a new website to showcase their services and reach more patients.',
      image: '/assets/images/mockups/THUMB-2.png',
      hasImage: true,
      figmaUrl: 'https://www.figma.com/deck/77PTu4QZZA84GW6d67E3GR/-PUBLIC--Dom-Murphy---Case-Studies-OCT-2025?node-id=69-9&t=wsKTALmEeHxEYry8-1'
    },
    {
      title: 'CRO for Marriott',
      year: '2025',
      body: 'Marriott hired me to improve the conversion rate of their booking flow on both their website and mobile app.',
      image: '/assets/images/mockups/THUMB-3.png',
      hasImage: true,
      figmaUrl: 'https://www.figma.com/deck/77PTu4QZZA84GW6d67E3GR/-PUBLIC--Dom-Murphy---Case-Studies-OCT-2025?node-id=69-27&t=wsKTALmEeHxEYry8-1'
    },
    {
      title: 'CRO for Athletic Greens',
      year: '2025',
      body: 'Athletic Greens hired me to improve the conversion rate of their product detail pages powered by Shopify.',
      image: '/assets/images/mockups/THUMB-4.png',
      hasImage: true,
      figmaUrl: 'https://www.figma.com/deck/77PTu4QZZA84GW6d67E3GR/-PUBLIC--Dom-Murphy---Case-Studies-OCT-2025?node-id=69-44&t=wsKTALmEeHxEYry8-1'
    }
  ], [])

  // Touch/drag handlers
  const handleStart = (e) => {
    setIsDragging(true)
    setStartX(e.touches ? e.touches[0].clientX : e.clientX)
  }
  
  const handleMove = (e) => {
    if (!isDragging) return
    const currentXPos = e.touches ? e.touches[0].clientX : e.clientX
    setCurrentX(currentXPos - startX)
  }
  
  const handleEnd = () => {
    if (!isDragging) return
    setIsDragging(false)
    setCurrentX(0)
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Track slider scroll progress
  useEffect(() => {
    const updateScrollProgress = () => {
      if (!sliderRef.current) return
      
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current
      const maxScroll = scrollWidth - clientWidth
      const progress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0
      setScrollProgress(progress)
    }

    const slider = sliderRef.current
    if (slider) {
      slider.addEventListener('scroll', updateScrollProgress)
      updateScrollProgress() // Initial calculation
    }

    return () => {
      if (slider) {
        slider.removeEventListener('scroll', updateScrollProgress)
      }
    }
  }, [])

  // Auto-timer effect
  useEffect(() => {
    const interval = setInterval(() => {
      // Only progress if not hovering (desktop only)
      if (!isHovering) {
        setProgress(prev => {
          if (prev >= 100) {
            setActiveService(prevService => {
              const currentIndex = services.indexOf(prevService)
              return services[(currentIndex + 1) % services.length]
            })
            return 0
          }
          return prev + 2 // 2% every 100ms = 5 seconds total
        })
      }
    }, 100)

    return () => clearInterval(interval)
  }, [services, isHovering])


  // Reset progress when service changes manually
  const handleServiceClick = (serviceId) => {
    setActiveService(serviceId)
    setProgress(0)
  }

  // Robust smooth-scroll for in-page nav links (accounts for sticky header)
  useEffect(() => {
    const header = document.querySelector('header[role="banner"]')
    if (!header) return
    const links = header.querySelectorAll('a[href^="#"]')

    const onClick = (e) => {
      const href = e.currentTarget.getAttribute('href') || ''
      const id = href.slice(1)
      // Don't handle 'privacy' - that routes to a different page
      if (id === 'privacy') return
      const target = document.getElementById(id)
      if (!id || !target) return
      e.preventDefault()
      const headerOffset = 80
      const rect = target.getBoundingClientRect()
      const y = window.pageYOffset + rect.top - headerOffset
      window.scrollTo({ top: y, behavior: 'smooth' })
      // Close mobile menu if open
      setIsMobileMenuOpen(false)
      const m = document.getElementById('mobileMenu')
      if (m) m.classList.add('hidden')
    }

    links.forEach((a) => a.addEventListener('click', onClick))
    return () => links.forEach((a) => a.removeEventListener('click', onClick))
  }, [])

  // Support direct hash navigation on load and on hashchange
  useEffect(() => {
    const headerOffset = 80
    const scrollToHash = () => {
      const hash = decodeURIComponent(window.location.hash || '').replace('#', '')
      if (!hash) return
      // Don't handle 'privacy' - that routes to a different page
      if (hash === 'privacy') return
      const el = document.getElementById(hash)
      if (!el) return
      // Wait for layout
      requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect()
        const y = window.pageYOffset + rect.top - headerOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      })
    }
    // On initial mount
    setTimeout(scrollToHash, 0)
    // On subsequent hash changes
    window.addEventListener('hashchange', scrollToHash)
    return () => window.removeEventListener('hashchange', scrollToHash)
  }, [])

  return (
    <div className="min-h-screen bg-white text-gray-900 selection:bg-black selection:text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 py-4" role="banner">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between h-16 rounded-full px-6 transition-all duration-250 ${
            isScrolled 
              ? 'bg-neutral-900 text-white shadow-lg' 
              : 'bg-white/80 backdrop-blur border border-neutral-200'
          }`}>
            <a href="#top" className={`font-semibold text-xl tracking-tight flex items-center gap-2 transition-all duration-250 ${
              isScrolled ? 'text-white' : 'text-black'
            }`}>
              {isScrolled ? (
                <LogoFull 
                  width={40} 
                  height={44} 
                  color="#ffffff"
                  className="flex-shrink-0"
                />
              ) : (
                <LogoMark 
                  width={32} 
                  height={18} 
                  color="#000000"
                  className="flex-shrink-0"
                />
              )}
            </a>
            <nav className="hidden md:flex items-center gap-6 text-sm" role="navigation" aria-label="Main navigation">
              <a href="#work" className={`hover:opacity-70 transition-opacity ${
                isScrolled ? 'text-white' : 'text-neutral-700'
              }`}>Work</a>
              <a href="#services" className={`hover:opacity-70 transition-opacity ${
                isScrolled ? 'text-white' : 'text-neutral-700'
              }`}>Services</a>
              <a href="#process" className={`hover:opacity-70 transition-opacity ${
                isScrolled ? 'text-white' : 'text-neutral-700'
              }`}>Process</a>
              <a href="#faq" className={`hover:opacity-70 transition-opacity ${
                isScrolled ? 'text-white' : 'text-neutral-700'
              }`}>FAQ</a>
              <Button 
                as="a" 
                href="https://cal.com/dommurphy/introduction" 
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                variant={isScrolled ? "white" : "primary"}
                className={isScrolled ? "bg-white text-neutral-900 hover:bg-neutral-100" : ""}
                data-click="navbar-book-call"
              >
                Book a call
              </Button>
            </nav>
            <div className="md:hidden flex items-center gap-3">
              <Button 
                as="a" 
                href="https://cal.com/dommurphy/introduction" 
                target="_blank"
                rel="noopener noreferrer"
                variant="secondary" 
                size="sm"
                className="text-sm"
                data-click="mobile-nav-book-call"
              >
                Book a call
              </Button>
              <button 
                id="menuBtn" 
                className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${
                  isScrolled 
                    ? 'bg-white text-neutral-900 hover:bg-neutral-100' 
                    : 'bg-neutral-900 text-white hover:bg-neutral-800'
                }`} 
                onClick={() => {
                  setIsMobileMenuOpen(prev => !prev)
                  const m = document.getElementById('mobileMenu');
                  if (m) m.classList.toggle('hidden');
                }}
                aria-label="Toggle mobile menu"
                aria-expanded={isMobileMenuOpen}
              >
                <span className="sr-only">Menu</span>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        {/* Mobile menu */}
        <div id="mobileMenu" className={`md:hidden ${isMobileMenuOpen ? '' : 'hidden'}`}>
          <div className="mx-4 mt-4 mb-6 bg-white rounded-2xl shadow-lg border border-neutral-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-100">
              <p className="text-sm font-medium text-neutral-500">Navigation</p>
            </div>
            <nav className="px-6 py-2">
              <div className="space-y-1">
                <a 
                  href="#work" 
                  onClick={()=>{ setIsMobileMenuOpen(false); const m = document.getElementById('mobileMenu'); if (m) m.classList.add('hidden');}}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Work
                </a>
                <a 
                  href="#services" 
                  onClick={()=>{ setIsMobileMenuOpen(false); const m = document.getElementById('mobileMenu'); if (m) m.classList.add('hidden');}}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Services
                </a>
                <a 
                  href="#process" 
                  onClick={()=>{ setIsMobileMenuOpen(false); const m = document.getElementById('mobileMenu'); if (m) m.classList.add('hidden');}}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  Process
                </a>
                
                <a 
                  href="#faq" 
                  onClick={()=>{ setIsMobileMenuOpen(false); const m = document.getElementById('mobileMenu'); if (m) m.classList.add('hidden');}}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  FAQ
                </a>
              </div>
              
              {/* CTA Button */}
              <div className="mt-4 pt-4 border-t border-neutral-100">
                <Button 
                  as="a" 
                  href="https://cal.com/dommurphy/introduction" 
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="primary" 
                  size="sm" 
                  className="w-full justify-center"
                  data-click="mobile-menu-book-call"
                >
                  Book a call
                </Button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative scroll-mt-24 lg:scroll-mt-28" role="main" aria-labelledby="hero-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="mx-auto max-w-3xl text-center">
            {/* Your photo */}
            <img 
              src="/assets/images/avatars/avatar.png" 
              alt="Dom - Conversion Rate Optimization Expert" 
              className="w-16 h-16 mx-auto mb-6 rounded-full object-cover border-2 border-neutral-200"
              loading="eager"
              width="64"
              height="64"
              decoding="async"
            />
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs sm:text-sm font-medium bg-neutral-100 ring-1 ring-neutral-200 text-neutral-700">
              <span>👋</span>
              <span>Dom - Conversion Rate Optimisation Expert</span>
            </div>
            <h1 id="hero-heading" className="composed-hero-title mt-16">
              <span className="inline-block">I turn more of your</span>
              <span className="composed-title-highlight clicks inline-flex items-center gap-2 mx-2 px-4 py-1 rounded-full bg-gradient-to-r from-orange-100 to-orange-200 border border-orange-300 shadow-sm hover:scale-105 transition-transform duration-300 -translate-y-0.5" aria-label="clicks">
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 80 80" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-orange-600 icon-animate-pulse icon-hover-scale"
                  style={{ animation: 'iconPulse 2s ease-in-out infinite' }}
                >
                  <path 
                    d="M10 40H20M40 10V20M26 26L18.6667 18.6667M54 26L61.3333 18.6667M26 54L18.6667 61.3333M40 40L70 50L56.6667 56.6667L50 70L40 40Z" 
                    stroke="currentColor" 
                    strokeWidth="6" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="font-semibold text-orange-800">clicks</span>
              </span>
              <span className="inline-block">into</span>
              <span className="composed-title-highlight customers inline-flex items-center gap-2 mx-2 px-4 py-1 rounded-full bg-gradient-to-r from-emerald-100 to-emerald-200 border border-emerald-300 shadow-sm hover:scale-105 transition-transform duration-300 -translate-y-0.5" aria-label="customers">
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 60 60" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-emerald-600 icon-animate-float icon-hover-rotate"
                  style={{ animation: 'iconFloat 3s ease-in-out infinite' }}
                >
                  <path 
                    fillRule="evenodd" 
                    clipRule="evenodd" 
                    d="M25 55C19.6957 55 14.6086 52.8929 10.8579 49.1421C7.10714 45.3914 5 40.3043 5 35C5 29.6957 7.10714 24.6086 10.8579 20.8579C14.6086 17.1071 19.6957 15 25 15C30.3043 15 35.3914 17.1071 39.1421 20.8579C42.8929 24.6086 45 29.6957 45 35C45 40.3043 42.8929 45.3914 39.1421 49.1421C35.3914 52.8929 30.3043 55 25 55ZM25 50C26.9698 50 28.9204 49.612 30.7403 48.8582C32.5601 48.1044 34.2137 46.9995 35.6066 45.6066C36.9995 44.2137 38.1044 42.5601 38.8582 40.7403C39.612 38.9204 40 36.9698 40 35C40 33.0302 39.612 31.0796 38.8582 29.2597C38.1044 27.4399 36.9995 25.7863 35.6066 24.3934C34.2137 23.0005 32.5601 21.8956 30.7403 21.1418C28.9204 20.388 26.9698 20 25 20C21.0218 20 17.2064 21.5804 14.3934 24.3934C11.5804 27.2064 10 31.0218 10 35C10 38.9782 11.5804 42.7936 14.3934 45.6066C17.2064 48.4196 21.0218 50 25 50ZM32.5 37.5C32.5 39.4891 31.7098 41.3968 30.3033 42.8033C28.8968 44.2098 26.9891 45 25 45C23.0109 45 21.1032 44.2098 19.6967 42.8033C18.2902 41.3968 17.5 39.4891 17.5 37.5H32.5ZM20 32.5C20.663 32.5 21.2989 32.2366 21.7678 31.7678C22.2366 31.2989 22.5 30.663 22.5 30C22.5 29.337 22.2366 28.7011 21.7678 28.2322C21.2989 27.7634 20.663 27.5 20 27.5C19.337 27.5 18.7011 27.7634 18.2322 28.2322C17.7634 28.7011 17.5 29.337 17.5 30C17.5 30.663 17.7634 31.2989 18.2322 31.7678C18.7011 32.2366 19.337 32.5 20 32.5ZM30 32.5C30.663 32.5 31.2989 32.2366 31.7678 31.7678C32.2366 31.2989 32.5 30.663 32.5 30C32.5 29.337 32.2366 28.7011 31.7678 28.2322C31.2989 27.7634 30.663 27.5 30 27.5C29.337 27.5 28.7011 27.7634 28.2322 28.2322C27.7634 28.7011 27.5 29.337 27.5 30C27.5 30.663 27.7634 31.2989 28.2322 31.7678C28.7011 32.2366 29.337 32.5 30 32.5ZM50 10H52.5C53.163 10 53.7989 10.2634 54.2678 10.7322C54.7366 11.2011 55 11.837 55 12.5C55 13.163 54.7366 13.7989 54.2678 14.2678C53.7989 14.7366 53.163 15 52.5 15H50V17.5C50 18.163 49.7366 18.7989 49.2678 19.2678C48.7989 19.7366 48.163 20 47.5 20C46.837 20 46.2011 19.7366 45.7322 19.2678C45.2634 18.7989 45 18.163 45 17.5V15H42.5C41.837 15 41.2011 14.7366 40.7322 14.2678C40.2634 13.7989 40 13.163 40 12.5C40 11.837 40.2634 11.2011 40.7322 10.7322C41.2011 10.2634 41.837 10 42.5 10H45V7.5C45 6.83696 45.2634 6.20107 45.7322 5.73223C46.2011 5.26339 46.837 5 47.5 5C48.163 5 48.7989 5.26339 49.2678 5.73223C49.7366 6.20107 50 6.83696 50 7.5V10Z" 
                    fill="currentColor"
                  />
                </svg>
                <span className="font-semibold text-emerald-800">customers</span>
              </span>
            </h1>
            <p className="mt-6 text-lg text-neutral-600">For 20 years, I've been designing and building websites that actually convert.</p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Button 
                as="a" 
                href="https://cal.com/dommurphy/introduction" 
                target="_blank"
                rel="noopener noreferrer"
                variant="primary" 
                size="lg"
                data-click="hero-book-call"
              >
                Book a call
              </Button>
              <Button as="a" href="#work" variant="secondary" size="lg">Success Stories</Button>
            </div>
            <p className="mt-4 text-xs text-neutral-500">98% of clients saw positive results within 72 hours.</p>
          </div>

                  {/* Logos */}
                  <div className="mt-16">
                    <p className="text-center text-sm font-medium text-neutral-500">Privileged to work with</p>
                    <div className="mt-6 grid grid-cols-6 gap-4 items-center justify-items-center max-w-4xl mx-auto">
                      {[
                        { name: "Amazon", logo: "/assets/images/logos/Amazon.svg" },
                        { name: "BCG", logo: "/assets/images/logos/BCG.svg" },
                        { name: "Crocs", logo: "/assets/images/logos/Crocs.svg" },
                        { name: "Marriott", logo: "/assets/images/logos/Marriott.svg" },
                        { name: "Sky", logo: "/assets/images/logos/Sky.svg" },
                        { name: "TikTok", logo: "/assets/images/logos/TikTok.svg" },
                      ].map((client) => (
                        <img 
                          key={client.name} 
                          src={client.logo} 
                          alt={`${client.name} logo`}
                          className="h-8 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity"
                          loading="lazy"
                          width="auto"
                          height="32"
                          decoding="async"
                        />
                      ))}
                    </div>
                  </div>
        </div>
      </section>

      {/* Work highlight */}
      <AnimatedSection delay={100} id="work" className="py-20 bg-neutral-50 scroll-mt-24 lg:scroll-mt-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <span className="inline-flex items-center rounded-full px-4 py-1 text-xs font-medium bg-neutral-100 ring-1 ring-neutral-200 text-neutral-700">Latest Work Highlight</span>
              <h2 className="mt-4 text-3xl sm:text-5xl font-bold tracking-tight font-heading">Recently completed projects</h2>
              <p className="mt-4 text-neutral-600">A few recent snapshots — more on request.</p>
            </div>
          </div>
      </AnimatedSection>

      {/* Full Width Case Studies Slider */}
      <div className="relative group/slider bg-neutral-50 pb-20">
        {/* Slider indicator text */}
        <div className="flex items-center justify-center mb-4 pt-8">
          <span className="text-xs text-neutral-400 font-medium tracking-wider uppercase">
            Swipe to explore
          </span>
        </div>
        
        <div 
          className="flex gap-8 overflow-x-auto scrollbar-hide pb-4 pl-4 pr-4 sm:pl-6 sm:pr-6 lg:pl-8 lg:pr-8"
          style={{ scrollSnapType: 'x mandatory', scrollPaddingLeft: '16px' }}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
          ref={sliderRef}
        >
          {projects.map((project, index) => (
            <div 
              key={project.title} 
              className="flex-shrink-0 w-80 md:w-96"
              style={{ scrollSnapAlign: index === 0 ? 'start' : 'start' }}
            >
              <div className="group relative">
                <div className="bg-white rounded-3xl shadow-sm transition-all duration-500 group-hover:scale-105 group/slider-hover:opacity-60 group-hover:opacity-100 overflow-hidden border border-neutral-100">
                  {/* Image Section */}
                  <div className="relative aspect-[16/10] overflow-hidden">
                    {project.hasImage ? (
                      <img 
                        src={project.image} 
                        alt={`${project.title} - Case study preview`}
                        className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                        loading="lazy"
                        width="400"
                        height="250"
                        decoding="async"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center">
                        <div className="text-neutral-400 text-sm font-medium px-4 text-center">
                          {project.image}
                        </div>
                      </div>
                    )}
                    
                    {/* Subtle overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* Year badge */}
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1">
                      <span className="text-xs font-semibold text-neutral-700">{project.year}</span>
                    </div>
                    
                    {/* View case study button - appears on hover */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                      <a
                        href={project.figmaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white text-neutral-900 px-6 py-3 rounded-full font-semibold text-sm shadow-lg hover:shadow-xl transition-all duration-250 hover:scale-105 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        View Case Study
                      </a>
                    </div>
                  </div>
                  
                  {/* Content Section */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-neutral-900 mb-2 group-hover:text-orange-600 transition-colors duration-250">
                      {project.title}
                    </h3>
                    <p className="text-neutral-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {project.body}
                    </p>
                    
                    {/* Client indicator */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Case Study</span>
                      </div>
                      <a
                        href={project.figmaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-500 hover:text-orange-600 text-sm font-medium transition-colors duration-200 flex items-center gap-1"
                      >
                        View Details
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Full-width progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-neutral-200">
          <div 
            className="h-full bg-neutral-600 transition-all duration-250 ease-out"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>
      </div>

      {/* Services */}
      <AnimatedSection delay={200} id="services" className="py-20 scroll-mt-24 lg:scroll-mt-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Left Column - Navigation */}
              <div className="lg:col-span-1">
                <div 
                  className="sticky top-24"
                  onMouseEnter={() => window.innerWidth >= 1024 && setIsHovering(true)}
                  onMouseLeave={() => window.innerWidth >= 1024 && setIsHovering(false)}
                >
                  {/* Desktop Header */}
                  <div className="hidden lg:block">
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight font-heading mb-4">Marketer designed, developer built.</h2>
                    <p className="text-neutral-600 mb-8">Here's my process for bridging the gap between marketers and developers.</p>
                  </div>
                  
                  {/* Mobile Header */}
                  <div className="lg:hidden mb-6">
                    <h2 className="text-2xl font-bold tracking-tight font-heading mb-2">Marketer designed, developer built.</h2>
                    <p className="text-neutral-600 text-sm">Here's my process for bridging the gap between marketers and developers.</p>
                  </div>
                  
                  {/* Desktop Navigation */}
                  <nav className="hidden lg:block space-y-2">
                    {[
                      {
                        id: 'designs',
                        title: 'Design that converts',
                        icon: (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                          </svg>
                        )
                      },
                      {
                        id: 'ui-ux',
                        title: 'Best practice UI/UX',
                        icon: (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        )
                      },
                      {
                        id: 'results',
                        title: 'See results in days',
                        icon: (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                        )
                      },
                      {
                        id: 'testing',
                        title: 'Find winning combinations',
                        icon: (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        )
                      }
                    ].map((item, index) => (
                      <button
                        key={item.id}
                        onClick={() => handleServiceClick(item.id)}
                        className={`w-full text-left p-4 rounded-xl flex flex-col gap-3 group relative overflow-hidden ${
                          activeService === item.id
                            ? 'bg-white border-2 border-neutral-800 text-neutral-900'
                            : 'bg-white border-2 border-neutral-200 text-neutral-700'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            activeService === item.id
                              ? 'bg-neutral-800 text-white'
                              : 'bg-neutral-100 text-neutral-600'
                          }`}>
                            {item.icon}
                          </div>
                          <span className="font-medium">{item.title}</span>
                        </div>
                        
                        {/* Progress indicator */}
                        {activeService === item.id && (
                          <div className="w-full flex items-center gap-2">
                            <div className="flex-1 h-1 bg-neutral-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-neutral-800 rounded-full transition-all duration-100 ease-linear"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                            {/* Pause indicator */}
                            {isHovering && (
                              <div className="flex items-center justify-center">
                                <svg 
                                  className="w-4 h-4 text-neutral-600" 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path d="M5.75 3a.75.75 0 0 0-.75.75v12.5c0 .414.336.75.75.75h1.5a.75.75 0 0 0 .75-.75V3.75A.75.75 0 0 0 7.25 3zm7 0a.75.75 0 0 0-.75.75v12.5c0 .414.336.75.75.75h1.5a.75.75 0 0 0 .75-.75V3.75a.75.75 0 0 0-.75-.75z" />
                                </svg>
                              </div>
                            )}
                          </div>
                        )}
                      </button>
                    ))}
                  </nav>
                  
                  {/* Mobile Navigation - Instagram Story Style */}
                  <nav className="lg:hidden">
                    <div className="flex space-x-4 overflow-x-auto pb-2 scrollbar-hide">
                      {[
                        {
                          id: 'designs',
                          title: 'Design that converts',
                          icon: (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          )
                        },
                        {
                          id: 'ui-ux',
                          title: 'Best practice UI/UX',
                          icon: (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          )
                        },
                        {
                          id: 'results',
                          title: 'See results in days',
                          icon: (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                          )
                        },
                        {
                          id: 'testing',
                          title: 'Find winning combinations',
                          icon: (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          )
                        }
                      ].map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleServiceClick(item.id)}
                          className={`flex flex-col items-center space-y-2 px-3 py-2 rounded-lg transition-all duration-200 flex-shrink-0 ${
                            activeService === item.id
                              ? 'bg-neutral-100'
                              : 'hover:bg-neutral-50'
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            activeService === item.id
                              ? 'bg-neutral-800 text-white'
                              : 'bg-neutral-100 text-neutral-600'
                          }`}>
                            {item.icon}
                          </div>
                          <span className={`text-xs font-medium text-center max-w-20 ${
                            activeService === item.id
                              ? 'text-neutral-900'
                              : 'text-neutral-600'
                          }`}>
                            {item.title}
                          </span>
                          {/* Active indicator */}
                          {activeService === item.id && (
                            <div className="w-full h-0.5 bg-neutral-800 rounded-full" />
                          )}
                        </button>
                      ))}
                    </div>
                    
                    {/* Mobile Progress Indicator */}
                    {activeService && (
                      <div className="mt-4 flex items-center gap-2">
                        <div className="flex-1 h-1 bg-neutral-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-neutral-800 rounded-full transition-all duration-100 ease-linear"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        {/* Pause indicator for mobile */}
                        {isHovering && (
                          <div className="flex items-center justify-center">
                            <svg 
                              className="w-4 h-4 text-neutral-600" 
                              xmlns="http://www.w3.org/2000/svg" 
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M5.75 3a.75.75 0 0 0-.75.75v12.5c0 .414.336.75.75.75h1.5a.75.75 0 0 0 .75-.75V3.75A.75.75 0 0 0 7.25 3zm7 0a.75.75 0 0 0-.75.75v12.5c0 .414.336.75.75.75h1.5a.75.75 0 0 0 .75-.75V3.75a.75.75 0 0 0-.75-.75z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    )}
                  </nav>
                </div>
              </div>

              {/* Right Column - Content */}
              <div className="lg:col-span-2">
                <div 
                  className="bg-white rounded-2xl p-8 border border-neutral-200 shadow-sm h-full flex flex-col relative overflow-hidden"
                  onMouseEnter={() => window.innerWidth >= 1024 && setIsHovering(true)}
                  onMouseLeave={() => window.innerWidth >= 1024 && setIsHovering(false)}
                >
                  <div className="flex-1 flex flex-col transition-all duration-500 ease-in-out relative overflow-hidden">
                    {activeService === 'designs' && (
                      <div className="flex-1 flex flex-col opacity-0 translate-x-4 animate-[fadeInSlide_0.5s_ease-out_forwards]">
                        <div className="mb-6">
                          <h3 className="text-xl font-semibold text-neutral-900 mb-3 opacity-0 translate-y-2 animate-[fadeInUp_0.3s_ease-out_0.1s_forwards]">Design that converts</h3>
                          <p className="text-neutral-600 leading-relaxed opacity-0 translate-y-2 animate-[fadeInUp_0.3s_ease-out_0.2s_forwards]">My process starts in Figma with UI/UX layouts, turning them into high-converting websites using Webflow after your approval.</p>
                        </div>
                        <div className="mt-auto flex justify-end">
                          <div className="w-96 h-96 rounded-lg overflow-hidden">
                            <video 
                              className="w-full h-full object-cover"
                              autoPlay
                              loop
                              muted
                              playsInline
                              preload="metadata"
                              poster="/assets/video/services/design-that-converts-poster.jpg"
                            >
                              <source src="/assets/video/services/design-that-converts.mp4" type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeService === 'ui-ux' && (
                      <div className="flex-1 flex flex-col opacity-0 translate-x-4 animate-[fadeInSlide_0.5s_ease-out_forwards]">
                        <div className="mb-6">
                          <h3 className="text-xl font-semibold text-neutral-900 mb-3 opacity-0 translate-y-2 animate-[fadeInUp_0.3s_ease-out_0.1s_forwards]">Best practice UI/UX</h3>
                          <p className="text-neutral-600 leading-relaxed opacity-0 translate-y-2 animate-[fadeInUp_0.3s_ease-out_0.2s_forwards]">All pages I create follow my winning formula for creating landing pages that get results. Download it here for free.</p>
                        </div>
                        <div className="mt-auto flex justify-end">
                          <div className="w-96 h-96 rounded-lg overflow-hidden">
                            <video 
                              className="w-full h-full object-cover"
                              autoPlay
                              loop
                              muted
                              playsInline
                              preload="metadata"
                              poster="/assets/video/services/best-practice-uiux-poster.jpg"
                            >
                              <source src="/assets/video/services/best-practice-uiux.mp4" type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeService === 'results' && (
                      <div className="flex-1 flex flex-col opacity-0 translate-x-4 animate-[fadeInSlide_0.5s_ease-out_forwards]">
                        <div className="mb-6">
                          <h3 className="text-xl font-semibold text-neutral-900 mb-3 opacity-0 translate-y-2 animate-[fadeInUp_0.3s_ease-out_0.1s_forwards]">See results in days, not weeks</h3>
                          <p className="text-neutral-600 leading-relaxed opacity-0 translate-y-2 animate-[fadeInUp_0.3s_ease-out_0.2s_forwards]">You don't need months or even weeks of testing to start seeing an increase in your conversions. 98% of clients saw positive results in 72 hours.</p>
                        </div>
                        <div className="mt-auto flex justify-end">
                          <div className="w-96 h-96 rounded-lg overflow-hidden">
                            <video 
                              className="w-full h-full object-cover"
                              autoPlay
                              loop
                              muted
                              playsInline
                              preload="metadata"
                              poster="/assets/video/services/results-in-days-poster.jpg"
                            >
                              <source src="/assets/video/services/results-in-days.mp4" type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeService === 'testing' && (
                      <div className="flex-1 flex flex-col opacity-0 translate-x-4 animate-[fadeInSlide_0.5s_ease-out_forwards]">
                        <div className="mb-6">
                          <h3 className="text-xl font-semibold text-neutral-900 mb-3 opacity-0 translate-y-2 animate-[fadeInUp_0.3s_ease-out_0.1s_forwards]">Find winning combinations</h3>
                          <p className="text-neutral-600 leading-relaxed opacity-0 translate-y-2 animate-[fadeInUp_0.3s_ease-out_0.2s_forwards]">I'll use split testing to find winning combinations of your headline, call to action and colour scheme.</p>
                        </div>
                        <div className="mt-auto flex justify-end">
                          <div className="w-96 h-96 rounded-lg overflow-hidden">
                            <video 
                              className="w-full h-full object-cover"
                              autoPlay
                              loop
                              muted
                              playsInline
                              preload="metadata"
                              poster="/assets/video/services/find-winning-combinations-poster.jpg"
                            >
                              <source src="/assets/video/services/find-winning-combinations.mp4" type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
      </AnimatedSection>

      {/* Testimonials */}
      <AnimatedSection delay={300} id="testimonials" className="py-20 bg-neutral-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-flex items-center rounded-full px-4 py-1 text-xs font-medium bg-neutral-100 ring-1 ring-neutral-200 text-neutral-700">Client Success Stories</span>
              <h2 className="mt-4 text-3xl sm:text-5xl font-bold tracking-tight font-heading">What clients say about working with me</h2>
              <p className="mt-4 text-neutral-600">Real results from real clients who've transformed their conversion rates.</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[
                                {
                                  name: "Lucrece Sicat",
                                  company: "Amazon",
                                  position: "Head of Creator Marketing",
                                  avatar: "/assets/images/avatars/ls-avatar.jpeg",
                                  linkedinUrl: "https://www.linkedin.com/in/lucrece-sicat-loeb-6aa2a694/",
                                  testimonial: "We are very pleased with the results we've seen from Dom's work. The landing pages are now more engaging and convert better."
                                },
                {
                  name: "Daniel Koek",
                  company: "DK Performance Coaching",
                  position: "Founder",
                  avatar: "/assets/images/avatars/daniel-koek.jpeg",
                  linkedinUrl: "https://www.linkedin.com/in/daniel-koek-9713249b/",
                  testimonial: "Dom transformed my landing pages from a technical and boring look to a modern and engaging. The results were immediate."
                },
                {
                  name: "Anabel Fielding",
                  company: "Alite Global",
                  position: "CEO",
                  avatar: "/assets/images/avatars/af-avatar.jpeg",
                  linkedinUrl: "https://www.linkedin.com/in/anabelfielding/",
                  testimonial: "Dom is an absolute superstar, a hands on IT dynamo and experienced tech solution designer, provider and fixer. A total joy to spend time with and an essential contact, he’s particularly calm, cheerful and resourceful in emergencies. Cannot recommend him highly enough."
                },

                {
                  name: "Bradley Bilgore",
                  company: "True Partnerships",
                  position: "Performance Coach",
                  avatar: "/assets/images/avatars/bb-avatar.jpeg",
                  linkedinUrl: "https://www.linkedin.com/in/brad-bilgore/",
                  testimonial: "The entire website design and subsequent tweaks look great and I'm already noticing a positive impact on enquiries."
                },
                {
                  name: "Laura Pettey",
                  company: "Sky",
                  position: "Senior Manager",
                  avatar: "/assets/images/avatars/lp-avatar.jpg",
                  testimonial: "Your whole team's expertise in both design and conversion optimisation is unmatched. We look forward to working with you guys again."
                },
                {
                  name: "Kay Sell",
                  company: "Hälsa Group",
                  position: "CEO",
                  avatar: "/assets/images/avatars/ks-avatar.jpeg",
                  linkedinUrl: "https://www.linkedin.com/in/kay-sell-9964a0179/",
                  testimonial: "We have been really happy with our marketing and design services that Dom has been doing for us and would recommend him and his team."
                }
              ].map((testimonial) => (
                <Testimonial
                  key={`${testimonial.name}-${testimonial.company}`}
                  name={testimonial.name}
                  company={testimonial.company}
                  position={testimonial.position}
                  avatar={testimonial.avatar}
                  linkedinUrl={testimonial.linkedinUrl}
                  testimonial={testimonial.testimonial}
                />
              ))}
            </div>
          </div>
      </AnimatedSection>

      {/* Process */}
      <AnimatedSection delay={300} id="process" className="py-20 bg-white scroll-mt-24 lg:scroll-mt-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-flex items-center rounded-full px-4 py-1 text-xs font-medium bg-neutral-100 ring-1 ring-neutral-200 text-neutral-700">What working with me will look like</span>
              <h2 className="mt-4 text-3xl sm:text-5xl font-bold tracking-tight font-heading">Here's my tried and tested process</h2>
              <p className="mt-4 text-neutral-600">A structured method for efficiency, ensuring precise results and consistent success.</p>
            </div>

            {/* Process Steps */}
            <div className="relative">
              {/* Connection Line - Desktop Only */}
              <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-neutral-200 transform -translate-y-1/2 z-0" />
              
              {/* Desktop Layout */}
              <div className="hidden lg:grid gap-8 lg:grid-cols-3 relative z-10">
                {[
                  { 
                    title: 'Book your free intro call', 
                    body: 'On this 15 minute call, we\'ll discuss your website and what challenges you currently face.',
                    icon: (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    ),
                    hasCTA: true
                  },
                  { 
                    title: 'Research and Wireframes', 
                    body: 'I\'ll begin my research process into your competitors, their offers and how we can beat them.',
                    icon: (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                    )
                  },
                  { 
                    title: 'Launch and Optimise', 
                    body: 'We\'ll launch your new designs and optimise for 60 days.',
                    icon: (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    )
                  },
                ].map((step, index) => (
                  <div key={index} className="relative flex">
                    {/* Card */}
                    <div className="bg-white rounded-2xl p-6 border border-neutral-200 shadow-sm flex flex-col w-full">
                      {/* Icon */}
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-neutral-100 text-neutral-600">
                        {step.icon}
                      </div>

                      {/* Content */}
                      <div className="flex flex-col flex-1">
                        <h3 className="text-lg font-semibold text-neutral-900 mb-3">
                          {step.title}
                        </h3>
                        <p className="text-neutral-600 leading-relaxed text-sm mb-4 flex-1">
                          {step.body}
                        </p>
                        {step.hasCTA && (
                          <Button 
                            as="a"
                            href="https://cal.com/dommurphy/introduction" 
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full mt-auto"
                            data-click="process-book-call"
                          >
                            Book Free Call
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile Layout - Horizontal Scroll */}
              <div className="lg:hidden">
                <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide" style={{ scrollSnapType: 'x mandatory' }}>
                  {[
                    { 
                      title: 'Book your free intro call', 
                      body: 'On this 15 minute call, we\'ll discuss your website and what challenges you currently face.',
                      icon: (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      ),
                      hasCTA: true
                    },
                    { 
                      title: 'Research and Wireframes', 
                      body: 'I\'ll begin my research process into your competitors, their offers and how we can beat them.',
                      icon: (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                      )
                    },
                    { 
                      title: 'Launch and Optimise', 
                      body: 'We\'ll launch your new designs and optimise for 60 days.',
                      icon: (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      )
                    },
                  ].map((step, index) => (
                    <div key={index} className="flex-shrink-0 w-80" style={{ scrollSnapAlign: 'start' }}>
                      {/* Compact Card */}
                      <div className="bg-white rounded-xl p-4 border border-neutral-200 shadow-sm h-full">
                        {/* Icon and Title Row */}
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-neutral-100 text-neutral-600 flex-shrink-0">
                            {step.icon}
                          </div>
                          <h3 className="text-sm font-semibold text-neutral-900 leading-tight">
                            {step.title}
                          </h3>
                        </div>

                        {/* Description */}
                        <p className="text-neutral-600 text-xs leading-relaxed mb-4">
                          {step.body}
                        </p>

                        {/* CTA Button */}
                        {step.hasCTA && (
                          <Button 
                            as="a"
                            href="https://cal.com/dommurphy/introduction" 
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            className="w-full text-xs"
                            data-click="process-book-call"
                          >
                            Book Free Call
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Mobile Scroll Indicator */}
                <div className="flex justify-center mt-4">
                  <div className="flex gap-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <div className="w-2 h-2 bg-neutral-300 rounded-full"></div>
                    <div className="w-2 h-2 bg-neutral-300 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>

         
           
          </div>
      </AnimatedSection>

      {/* About Me */}
      <AnimatedSection delay={350} id="about" className="py-20 bg-neutral-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Column - Text Content */}
            <div className="space-y-8">
              {/* Main Heading */}
              <div className="space-y-4">
                <h2 className="text-4xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-heading leading-tight">
                  What building websites for 20 years has taught me
                </h2>
                <p className="text-xl text-neutral-300 leading-relaxed">
                  A beautiful website isn't enough. It needs to convert.
                </p>
              </div>

              {/* Single Column Text */}
              <div className="text-neutral-300 leading-relaxed space-y-4">
                <p>
                  Most <span className="text-white font-semibold">developers</span> can't market, and most <span className="text-white font-semibold">marketers</span> can't code - leaving everyone else stuck in the middle.
                </p>
                <p>
                  Developers build brilliant, functional websites but often miss the psychological triggers that drive user action. Marketers understand persuasion and user behaviour but struggle to implement their ideas without technical know-how.
                </p>
                <p>
                  I combine <span className="text-white font-semibold bg-neutral-800 px-2 py-1 rounded">deep technical knowledge</span> with <span className="text-white font-semibold bg-neutral-800 px-2 py-1 rounded">proven marketing psychology</span>, allowing me to implement conversion-focused solutions without the usual back-and-forth between teams.
                </p>
                <p>
                  I speak both languages fluently, translating customer behaviour insights into technical <span className="text-white font-semibold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">implementations that actually work</span>.
                </p>
              </div>

              {/* Signature */}
              <div className="flex flex-col items-start space-y-2">
                <img 
                  src="/assets/images/dmsig.png" 
                  alt="Dom Murphy Signature" 
                  className="h-12 w-auto"
                  loading="lazy"
                  width="auto"
                  height="48"
                  decoding="async"
                />
                <div className="text-neutral-400">Conversion Rate Optimisation Expert</div>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="flex justify-end">
              <div className="w-96 h-[500px] overflow-hidden">
                <img 
                  src="/assets/images/photos/domportrait.jpg" 
                  alt="Dom Murphy - Conversion Rate Optimisation Expert" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="384"
                  height="500"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Interactive Goal Selector */}
      <AnimatedSection delay={400} id="goals" className="py-20 bg-neutral-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-flex items-center rounded-full px-4 py-1 text-xs font-medium bg-white ring-1 ring-neutral-200 text-neutral-700 shadow-sm">Choose Your Path</span>
              <h2 className="mt-4 text-3xl sm:text-5xl font-bold tracking-tight font-heading">Let's start with your goal</h2>
              <p className="mt-4 text-neutral-600">Select your primary objective to see how I can help you achieve it.</p>
            </div>

            {/* Interactive Goal Cards */}
            <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Lead Generation Card */}
              <div className="group relative bg-white rounded-3xl border-2 border-neutral-200 shadow-lg hover:shadow-xl transition-all duration-500 hover:border-orange-300 hover:-translate-y-2 overflow-hidden">
                {/* Header with Icon */}
                <div className="relative p-8 pb-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-neutral-900 group-hover:text-orange-600 transition-colors duration-300">Lead Generation</h3>
                      <p className="text-sm text-orange-600 font-medium">Capture more qualified leads</p>
                    </div>
                  </div>
                  
                  <p className="text-neutral-600 leading-relaxed text-lg mb-6">
                    Looking to get <span className="text-orange-600 font-semibold">more leads?</span> I'll optimize your landing pages, forms, and implement A/B testing to find winning combinations.
                  </p>

                  {/* Benefits List */}
                  <div className="space-y-3 mb-8">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span className="text-neutral-700">Landing page optimization</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span className="text-neutral-700">Form conversion improvements</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span className="text-neutral-700">A/B split testing</span>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="px-8 pb-6">
                  <div className="bg-neutral-100 rounded-2xl p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">+28%</div>
                        <div className="text-xs text-neutral-600">Average increase in conversion rate*</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">72hrs</div>
                        <div className="text-xs text-neutral-600">Average time to see results*</div>
                      </div>
                    </div>
                    <p className="text-xs text-neutral-500 mt-3 text-center">*Collected from a survey of clients between March 2022 and March 2025.</p>
                  </div>
                </div>


                {/* Decorative Element */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100/50 to-orange-200/50 rounded-full -translate-y-16 translate-x-16 group-hover:scale-110 transition-transform duration-500"></div>
              </div>

              {/* E-commerce Card */}
              <div className="group relative bg-white rounded-3xl border-2 border-neutral-200 shadow-lg hover:shadow-xl transition-all duration-500 hover:border-emerald-300 hover:-translate-y-2 overflow-hidden">
                {/* Header with Icon */}
                <div className="relative p-8 pb-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-neutral-900 group-hover:text-emerald-600 transition-colors duration-300">E-commerce</h3>
                      <p className="text-sm text-emerald-600 font-medium">Increase checkout conversions</p>
                    </div>
                  </div>
                  
                  <p className="text-neutral-600 leading-relaxed text-lg mb-6">
                    Looking to get more checkouts on your <span className="text-emerald-600 font-semibold">eCommerce website?</span> I'll optimize your product pages, checkout flow, and cart abandonment workflows.
                  </p>

                  {/* Benefits List */}
                  <div className="space-y-3 mb-8">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      <span className="text-neutral-700">Product page optimization</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      <span className="text-neutral-700">Checkout flow improvements</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      <span className="text-neutral-700">Cart abandonment recovery</span>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="px-8 pb-6">
                  <div className="bg-neutral-100 rounded-2xl p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">18%</div>
                        <div className="text-xs text-neutral-600">Additional revenue per customer*</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">14%</div>
                        <div className="text-xs text-neutral-600">Average increase in checkout rate*</div>
                      </div>
                    </div>
                    <p className="text-xs text-neutral-500 mt-3 text-center">*Collected from a survey of clients between March 2022 and March 2025.</p>
                  </div>
                </div>


                {/* Decorative Element */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-100/50 to-emerald-200/50 rounded-full -translate-y-16 translate-x-16 group-hover:scale-110 transition-transform duration-500"></div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-16">
              {/* Desktop CTA */}
              <div className="hidden sm:block text-center">
                <div className="inline-flex items-center gap-3 bg-white rounded-2xl px-8 py-4 shadow-lg border border-neutral-200 hover:shadow-xl transition-shadow duration-300">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-lg font-medium text-neutral-700">Let's increase your conversion rate</span>
                  <Button 
                    as="a" 
                    href="https://cal.com/dommurphy/introduction" 
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="primary" 
                    size="lg"
                    data-click="goal-selector-book-call"
                  >
                    Book Your free 15-Minute Call
                  </Button>
                </div>
              </div>

              {/* Mobile CTA */}
              <div className="sm:hidden">
                <div className="bg-white rounded-2xl p-4 shadow-lg border border-neutral-200">
                  {/* Mobile Header */}
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-neutral-700">Let's increase your conversion rate</span>
                  </div>
                  
                  {/* Mobile Button */}
                  <Button 
                    as="a" 
                    href="https://cal.com/dommurphy/introduction" 
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="primary" 
                    size="lg"
                    className="w-full"
                    data-click="goal-selector-book-call"
                  >
                    Book Your free 15-Minute Call
                  </Button>
                </div>
              </div>
            </div>
          </div>
      </AnimatedSection>

    

      {/* FAQ */}
      <AnimatedSection delay={600} id="faq" className="py-20 bg-white scroll-mt-24 lg:scroll-mt-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-flex items-center rounded-full px-4 py-1 text-xs font-medium bg-neutral-100 ring-1 ring-neutral-200 text-neutral-700">Frequently Asked Questions</span>
              <h2 className="mt-4 text-3xl sm:text-5xl font-bold tracking-tight font-heading">Straight answers so you can decide fast</h2>
              <p className="mt-4 text-neutral-600">Everything you need to know about working with me.</p>
            </div>

            <div className="grid gap-4 max-w-4xl mx-auto">
              {[
                {
                  q: 'What exactly will you do to my website?',
                  a: 'I’ll use my two decades of website design and development experience to help you get more of what matters. Whether that is more bookings, more qualified leads or fewer abandoned checkouts, I will refine the design, messaging and user journey so your site does the heavy lifting for you.',
                  icon: (
                    // sliders (adjustments)
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
                    </svg>
                  )
                },
                {
                  q: 'What sectors do you specialise in?',
                  a: 'I specialise in lead generation and eCommerce. If your business relies on turning traffic into enquiries or sales, that is where I shine.',
                  icon: (
                    // info/question
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )
                },
                {
                  q: 'Can you build me a website from scratch?',
                  a: 'I can, however my pricing starts from £2,500 per page. To keep things efficient, I recommend working with a Webflow developer and bringing me in for strategy and design so we get the best result for your budget.',
                  icon: (
                    // document edit
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  )
                },
                {
                  q: 'How much do you charge?',
                  a: 'My pricing is fixed and straightforward. I charge per page, starting from £2,500 depending on scope and complexity.',
                  icon: (
                    // clock (simple, consistent styling)
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )
                },
                {
                  q: 'Is there a guarantee?',
                  a: 'Yes. If we agree a measurable conversion goal before we start and you do not see an improvement, I will continue working without charge until you do. You will need accurate tracking set up so we have a fair comparison point.',
                  icon: (
                    // shield check
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )
                },
                {
                  q: "Isn't Conversion Rate Optimisation just split testing?",
                  a: 'Split testing is one small part of CRO. Real optimisation is much more involved. I refine everything that affects results: design, copy, user experience, offer clarity and follow up systems like email and SMS. The aim is simple. Turn more of your traffic into paying customers, consistently.',
                  icon: (
                    // question/info
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )
                },
              ].map((faq) => (
                <FAQItem key={faq.q} question={faq.q} answer={faq.a} icon={faq.icon} />
              ))}
            </div>
          </div>
      </AnimatedSection>

      {/* Contact */}
      <AnimatedSection delay={700} id="contact" className="py-20 bg-neutral-900 text-white rounded-t-[2.5rem] scroll-mt-24 lg:scroll-mt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl sm:text-5xl font-bold tracking-tight font-heading">Let's increase your conversion rate.</h2>            
              <p className="mt-4 text-white/70">Book a free 15-minute call to discuss your website goals and challenges.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button 
                  as="a" 
                  href="https://cal.com/dommurphy/introduction" 
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="white" 
                  size="lg"
                  data-click="contact-book-call"
                >
                  Book a Call
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-center h-full">
              <LogoFull className="h-full text-white" />
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Footer */}
      <footer className="py-10" role="contentinfo">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <p className="text-sm text-neutral-500">© {new Date().getFullYear()} Dom. All rights reserved.</p>
          <div className="text-sm text-neutral-500 flex items-center gap-4">
            <a href="#top" className="hover:text-black/70">Back to top</a>
            <Link to="/privacy" className="hover:text-black/70">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
